import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import { debounce } from "lodash";
import {
  EmptyLayout,
  Footer,
  SearchBar,
  SuggestionBar,
  Table,
} from "../../components/molecules";
import { actions } from "./dataModels.reducers";
import { selectCollections, selectSelectedDataModel } from "./index";
import { chevronRightIcon, dbSidePanelIcon } from "../../assets/icons";
import { FOOTER_CONSTANTS, SEARCH_DEBOUNCE_TIME, TABS } from "../../constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { renderNoDataModelLayout } from "../../helpers";

const { setSelectedDataTable, setSelectedDataModelId, setCollectionsFilters } =
  actions;
const { setSelectedTab } = breadCrumbActions;
const { DATA_MODELS_TABLES_TITLE, DATA_MODELS_TABLES_SUBTITLE } =
  FOOTER_CONSTANTS;
const { COLLECTIONS } = TABS;
const { FETCH_COLLECTIONS } = SPINNER_NAMES;
const MIN_HEIGHT = "min-h-[50vh]";
const HEIGHT = "h-[50vh]";

const DataModelData = () => {
  const { dataModelId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: collections, total: totalCollections } =
    useSelector(selectCollections);
  const selectedDataModel = useSelector(selectSelectedDataModel);

  const [tables, setTables] = useState([]);
  const [isSpinnerActive, setIsSpinnerActive] = useState();
  const height = isSpinnerActive ? HEIGHT : MIN_HEIGHT;

  useEffect(() => {
    dispatch(setSelectedTab(selectedDataModel));
  }, [dispatch, selectedDataModel]);

  useEffect(() => {
    if (dataModelId) dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId]);

  useEffect(() => {
    const formattedCollections = collections.map((each) => {
      return { ...each, properties: each.schema.properties.length };
    });
    setTables(formattedCollections);
  }, [collections]);

  useEffect(() => {
    return () => {
      dispatch(setCollectionsFilters());
    };
  }, [dispatch]);

  const IconFormatter = useCallback(
    () => (
      <img
        src={chevronRightIcon}
        width="20"
        alt="chevron-right-icon"
        className="hover:opacity-70"
      />
    ),
    []
  );

  const columns = [
    {
      dataField: "name",
      text: "TABLES",
      width: 610,
    },
    {
      dataField: "rows",
      text: "ROWS",
      width: 200,
      className: "text-center",
    },
    {
      dataField: "properties",
      text: "Properties",
      width: 200,
      className: "text-center",
    },
    {
      formatter: IconFormatter,
      width: 70,
      className: "min-w-[20px] !px-1 !py-1",
    },
  ];

  const handleOnRowClick = (row) => {
    dispatch(setSelectedDataTable(row));
    navigate(`/data-models/${dataModelId}/data/${row.id}/${row.refName}`);
  };

  const handleOnSearch = debounce(
    (name) => dispatch(setCollectionsFilters({ name })),
    SEARCH_DEBOUNCE_TIME
  );

  const renderCollectionData = () =>
    tables.length ? (
      <Table columns={columns} data={tables} onRowClick={handleOnRowClick} />
    ) : (
      <EmptyLayout
        title="No Table to display"
        shouldShowButton={false}
        image={dbSidePanelIcon}
      />
    );

  return (
    <div className="content scrollbar overflow-auto p-6 flex-1">
      <Helmet>
        <title>Data | TavaJS</title>
      </Helmet>
        <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4">
          {!!totalCollections && (
            <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
          )}
      </div>
      {selectedDataModel && (
        <SuggestionBar
          text="Manage your tables on"
          link={COLLECTIONS}
          handleOnClick={() =>
            navigate(`/data-models/${dataModelId}/collections`)
          }
          classes="text-gray-200"
        />
      )}
      <div className={classNames(height)}>
        <Spinner name={FETCH_COLLECTIONS} setIsSpinnerActive={setIsSpinnerActive}>
          {selectedDataModel ? renderCollectionData() : renderNoDataModelLayout(navigate)}
        </Spinner>
      </div>
      {selectedDataModel && <Footer
        title={DATA_MODELS_TABLES_TITLE}
        subtitle={DATA_MODELS_TABLES_SUBTITLE}
      />}
    </div>
  );
};

export default DataModelData;
